const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5000/api';

class ApiService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };
  }

  async request(endpoint: string, options: RequestInit = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const config: RequestInit = {
      headers: this.getAuthHeaders(),
      ...options
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
        const error = new Error(`HTTP error! status: ${response.status}`);
        (error as any).response = { data: errorData };
        throw error;
      }
      
      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Pacientes
  async getPacientes() {
    return this.request('/pacientes');
  }

  async searchPacientes(query: string) {
    return this.request(`/pacientes/search?q=${encodeURIComponent(query)}`);
  }

  async getPaciente(id: string) {
    return this.request(`/pacientes/${id}`);
  }

  async createPaciente(data: any) {
    return this.request('/pacientes', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updatePaciente(id: string, data: any) {
    return this.request(`/pacientes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deletePaciente(id: string) {
    return this.request(`/pacientes/${id}`, {
      method: 'DELETE'
    });
  }

  // Avaliações
  async getAvaliacoes(pacienteId: string, tipo: 'anamnese' | 'fisica') {
    return this.request(`/avaliacoes/${pacienteId}/${tipo}`);
  }

  async createAvaliacao(data: any) {
    return this.request('/avaliacoes', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getAvaliacao(id: string) {
    return this.request(`/avaliacoes/${id}`);
  }

  // Auth
  async login(email: string, password: string) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, senha: password })
    });
  }

  async register(data: any) {
    return this.request('/auth/registro', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Agendamentos
  async getAgendamentos() {
    return this.request('/agendamentos');
  }

  // Consultas
  async getConsultas() {
    return this.request('/consultas');
  }

  async createConsulta(data: { pacienteNome: string; dataHora: string; modalidade?: string }) {
    return this.request('/consultas', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async deleteConsulta(id: string) {
    return this.request(`/consultas/${id}`, {
      method: 'DELETE'
    });
  }

  async approveConsulta(id: string) {
    return this.request(`/consultas/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'aprovada' })
    });
  }

  // Google Calendar
  async getGoogleCalendarEvents() {
    return this.request('/google-calendar/events');
  }

  // Alterar senha
  async alterarSenha(senhaAtual: string, novaSenha: string, confirmarSenha: string) {
    return this.request('/auth/alterar-senha', {
      method: 'PUT',
      body: JSON.stringify({ senhaAtual, novaSenha, confirmarSenha })
    });
  }

  // Alterar senha padrão (sem senha atual)
  async alterarSenhaPadrao(email: string, novaSenha: string, confirmarSenha: string) {
    return this.request('/auth/alterar-senha-padrao', {
      method: 'PUT',
      body: JSON.stringify({ email, novaSenha, confirmarSenha })
    });
  }

  // Verificar senha padrão
  async verificarSenhaPadrao() {
    return this.request('/auth/verificar-senha-padrao');
  }

  // Verificar senha padrão (temporário)
  async verificarSenhaPadraoTemp(email: string) {
    return this.request('/auth/verificar-senha-padrao-temp', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }
}

export const apiService = new ApiService();